import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Plus, Search, Filter, ChevronDown, X, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { MessageSquare, User, Calendar, Clock, Eye, Download, Trash2, FileText, FileImage, FileVideo, FileAudio, FileArchive, FileCode, Pencil, History } from 'lucide-react';
import { formatPhoneNumber, getRelativeTime } from '../../utils/formatters';
import { getEntryTypeDisplayInfo, formatFileSize, formatDuration, getEntryTypeIcon, getEntryTypeIconColor, getFileTypeBadge } from '../../utils/timelineMetadata';
import { JobContactEntry, EntryType, WorkOrder } from '../../types';
import { apiService } from '../../services/api';
import { isVideoFileName } from '../../utils/jobAttachments';

interface TimelineTabProps {
  entries: JobContactEntry[];
  entryTypes: EntryType[];
  workOrder: WorkOrder;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  entryTypeFilter: number | null;
  setEntryTypeFilter: (filter: number | null) => void;
  vendorFilter: string;
  setVendorFilter: (filter: string) => void;
  onAddEntry: () => void;
  formatTimestamp: (date: string) => string;
  onFileDownload?: (filePath: string, fileName: string) => void;
  onViewImage?: (filePath: string, fileName: string) => void;
  onDeleteAttachment?: (attachment: JobContactAttachment) => void;
  onEditEntry?: (entry: JobContactEntry) => void;
  onViewHistory?: (entry: JobContactEntry) => void;
  editedEntryIds?: number[];
}

const TimelineTab: React.FC<TimelineTabProps> = ({
  entries,
  entryTypes,
  workOrder,
  searchTerm,
  setSearchTerm,
  showFilters,
  setShowFilters,
  entryTypeFilter,
  setEntryTypeFilter,
  vendorFilter,
  setVendorFilter,
  onAddEntry,
  formatTimestamp,
  onFileDownload,
  onViewImage,
  onDeleteAttachment,
  onEditEntry,
  onViewHistory,
  editedEntryIds,
}) => {
  // State for popovers
  const [showSearchPopover, setShowSearchPopover] = useState(false);
  const [showFilterPopover, setShowFilterPopover] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  // Close popovers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchPopover(false);
      }
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilterPopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Count active filters
  const activeFilterCount = (entryTypeFilter !== null ? 1 : 0) + (vendorFilter ? 1 : 0);

  // Get file type icon
  const getFileTypeIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().split('.').pop();
    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'webp':
      case 'svg':
        return { Icon: FileImage, className: 'text-green-500' };
      case 'mp4':
      case 'm4v':
      case 'avi':
      case 'mov':
      case 'webm':
      case 'wmv':
      case 'flv':
        return { Icon: FileVideo, className: 'text-purple-500' };
      case 'mp3':
      case 'wav':
      case 'flac':
      case 'aac':
        return { Icon: FileAudio, className: 'text-blue-500' };
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return { Icon: FileArchive, className: 'text-orange-500' };
      case 'js':
      case 'ts':
      case 'html':
      case 'css':
      case 'json':
      case 'xml':
        return { Icon: FileCode, className: 'text-indigo-500' };
      default:
        return { Icon: FileText, className: 'text-gray-500' };
    }
  };

  // Check if file is an image
  const isImageFile = (fileName: string): boolean => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  };

  // Image Thumbnail Component that loads via API
  const ImageThumbnail: React.FC<{ filePath: string; fileName: string; attachmentId: number }> = ({ filePath, fileName, attachmentId }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const hasRequestedRef = useRef(false);

    useEffect(() => {
      let isMounted = true;
      let observer: IntersectionObserver | null = null;

      const loadImage = async () => {
        try {
          const blob = await apiService.downloadFile(filePath);
          if (!isMounted) return;
          const url = window.URL.createObjectURL(blob);
          setImageUrl(url);
          setLoading(false);
        } catch (err) {
          console.error('Failed to load thumbnail:', filePath, err);
          if (isMounted) {
            setError(true);
            setLoading(false);
          }
        }
      };

      const observe = () => {
        if (!containerRef.current || hasRequestedRef.current) return;
        observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && !hasRequestedRef.current) {
            hasRequestedRef.current = true;
            loadImage();
            observer && observer.disconnect();
          }
        }, { rootMargin: '200px' });
        observer.observe(containerRef.current);
      };

      observe();

      return () => {
        isMounted = false;
        if (observer) observer.disconnect();
        if (imageUrl) {
          window.URL.revokeObjectURL(imageUrl);
        }
      };
    }, [filePath]);

    if (loading) {
      return (
        <div ref={containerRef} className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
          <div className="animate-pulse text-gray-400 dark:text-gray-600">Loading...</div>
        </div>
      );
    }

    if (error || !imageUrl) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
          <FileImage className="h-8 w-8 text-gray-400 dark:text-gray-600" />
        </div>
      );
    }

    return (
      <img
        src={imageUrl}
        alt={fileName}
        className="w-full h-full object-cover"
      />
    );
  };
  return (
    <div className="space-y-6">
      {/* Header with Search/Filter Icons and Add Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Activity Timeline</h2>
            <Badge variant="outline">{entries.length}</Badge>
          </div>

          {/* Search and Filter Icons */}
          <div className="flex items-center space-x-2">
            {/* Search Icon with Popover */}
            <div className="relative" ref={searchRef}>
              <button
                onClick={() => {
                  setShowSearchPopover(!showSearchPopover);
                  setShowFilterPopover(false);
                }}
                className={`p-2 rounded-md transition-colors ${
                  searchTerm
                    ? 'bg-accent-color text-white hover:bg-accent-color-600'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                title="Search timeline"
              >
                <Search className="h-4 w-4" />
              </button>

              {showSearchPopover && (
                <div className="absolute top-12 left-0 z-50 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Search Timeline</h3>
                    <button
                      onClick={() => setShowSearchPopover(false)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <Input
                      type="text"
                      placeholder="Search timeline entries..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      autoFocus
                    />
                  </div>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="mt-2 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Filter Icon with Popover */}
            <div className="relative" ref={filterRef}>
              <button
                onClick={() => {
                  setShowFilterPopover(!showFilterPopover);
                  setShowSearchPopover(false);
                }}
                className={`p-2 rounded-md transition-colors relative ${
                  activeFilterCount > 0
                    ? 'bg-accent-color text-white hover:bg-accent-color-600'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                title="Filter timeline"
              >
                <Filter className="h-4 w-4" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {showFilterPopover && (
                <div className="absolute top-12 left-0 z-50 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Filter Timeline</h3>
                    <button
                      onClick={() => setShowFilterPopover(false)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Entry Type
                      </label>
                      <Select value={entryTypeFilter?.toString() || "all"} onValueChange={(value) => setEntryTypeFilter(value === "all" ? null : parseInt(value))}>
                        <SelectTrigger>
                          <SelectValue placeholder="All types" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All types</SelectItem>
                          {entryTypes.map((entryType) => (
                            <SelectItem key={entryType.entryTypeId} value={entryType.entryTypeId.toString()}>
                              {entryType.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Vendor
                      </label>
                      <Select value={vendorFilter || "all"} onValueChange={(value) => setVendorFilter(value === "all" ? "" : value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="All vendors" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All vendors</SelectItem>
                          {workOrder?.vendors?.map((vendor) => (
                            <SelectItem key={vendor.vendorId} value={vendor.vendorId.toString()}>
                              {vendor.vendorName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {activeFilterCount > 0 && (
                    <button
                      onClick={() => {
                        setEntryTypeFilter(null);
                        setVendorFilter('');
                      }}
                      className="mt-4 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <Button
          onClick={onAddEntry}
          className="flex items-center space-x-2 bg-accent-color hover:bg-accent-color-600 text-white"
        >
          <Plus className="h-4 w-4" />
          <span>Add Timeline Entry</span>
        </Button>
      </div>

      {/* Timeline Entries */}
      <Card>
        <CardContent className="p-6 flex flex-col h-[calc(100vh-200px)]">
          <div className="space-y-4 overflow-y-auto min-h-0 flex-1 pr-2">
            {entries.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <div className="text-sm">No timeline entries yet</div>
                <div className="text-xs">Add the first entry to start the conversation</div>
              </div>
            ) : (
              entries.map((entry, index) => {
                const timestamp = formatTimestamp(entry.createdDate);
                const isEdited = Boolean(
                  (entry as any).updatedDate ||
                  (entry as any).updatedAt ||
                  (entry as any).editedDate ||
                  (entry as any).updatedBy ||
                  (editedEntryIds && editedEntryIds.includes(entry.id))
                );
                return (
                  <div key={entry.id} className={`relative rounded-lg overflow-hidden ${entry.isBilled ? 'bg-green-50 dark:bg-green-900/10 border-l-4 border-green-400' : ''}`}>

                    <div className="flex items-start space-x-3 pt-2">
                      {/* Entry Type Icon */}
                      <div className="flex-shrink-0 relative w-10 self-stretch">
                        {/* Timeline dotted line - centered under icon */}
                        {index < entries.length - 1 && (
                          <div className="absolute left-1/2 -translate-x-1/2 top-10 bottom-0 w-px border-l-2 border-dotted border-gray-300 dark:border-gray-600"></div>
                        )}
                        {(() => {
                          const Icon = getEntryTypeIcon(entry.entryTypeId);
                          const iconColor = getEntryTypeIconColor(entry.entryTypeId);
                          return (
                            <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                              <Icon className={`h-5 w-5 ${iconColor}`} />
                            </div>
                          );
                        })()}
                      </div>

                      {/* Content - add padding to align with billable items' border */}
                      <div className={`flex-1 min-w-0 ${entry.isBilled ? 'pl-4' : 'pl-4'}`}>
                        {(() => {
                          const displayInfo = getEntryTypeDisplayInfo(entry);
                          return (
                            <>
                              {/* Entry Type Label and User Info */}
                              <div className="flex items-center space-x-2 mb-1 group">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                  {displayInfo.badge}
                                </span>
                                {isEdited && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400">
                                    Edited
                                  </span>
                                )}
                                {/* Billing Status Indicator */}
                                {entry.isBilled && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Billed
                                    {entry.invoiceNumbers && entry.invoiceNumbers.length > 0 && (
                                      <span className="ml-1">
                                        on Invoice {entry.invoiceNumbers.join(', ')}
                                      </span>
                                    )}
                                  </span>
                                )}
                                <span className="text-xs text-gray-400 dark:text-gray-500">•</span>
                                <div className="flex items-center space-x-2">
                                  {entry.userProfilePhotoUrl ? (
                                    <img
                                      src={(() => {
                                        const backendBaseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || `${window.location.protocol}//${window.location.hostname}:5269`;
                                        return `${backendBaseUrl}/uploads/${entry.userProfilePhotoUrl}`;
                                      })()}
                                      alt={`${entry.userName || 'User'}`}
                                      className="w-5 h-5 rounded-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                      }}
                                    />
                                  ) : (
                                    <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                                      <User className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                                    </div>
                                  )}
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {entry.userName || `${entry.user?.firstName || ''} ${entry.user?.lastName || ''}`.trim() || 'Unknown User'}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500">•</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {timestamp.relative}
                                </span>
                                {/* Edit button - subtle and shown on hover */}
                                <button
                                  onClick={() => onEditEntry && onEditEntry(entry)}
                                  className="ml-2 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-100 dark:hover:bg-gray-700"
                                  title="Edit"
                                  aria-label="Edit timeline entry"
                                >
                                  <Pencil className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                </button>
                                {isEdited && (
                                  <button
                                    onClick={() => onViewHistory && onViewHistory(entry)}
                                    className="ml-1 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 dark:text-gray-400"
                                    title="View edit history"
                                    aria-label="View edit history"
                                  >
                                    <History className="h-4 w-4" />
                                  </button>
                                )}
                                {entry.vendorId && (
                                  <>
                                    <span className="text-xs text-gray-400 dark:text-gray-500">→</span>
                                    <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                                      {workOrder?.vendors?.find(v => v.vendorId === entry.vendorId)?.vendorName}
                                    </span>
                                  </>
                                )}
                              </div>
                              
                              {/* Main Content */}
                              <div className="mb-2">
                                {/* Only show title if it's different from the badge/entry type name */}
                                {displayInfo.title && displayInfo.title.toLowerCase() !== displayInfo.badge.toLowerCase() && (
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                      {displayInfo.title}
                                    </span>
                                    {/* Show response required warning next to title for Vendor/Client Messages */}
                                    {((entry.entryTypeId === 7 || entry.entryTypeId === 8) && displayInfo.responseRequired) && (
                                      <span className="flex items-center space-x-1 px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                                        <span className="text-xs">⚠️</span>
                                        <span className="text-xs font-medium">Response Required</span>
                                      </span>
                                    )}
                                  </div>
                                )}
                                
                                {displayInfo.subtitle && (
                                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    {displayInfo.subtitle}
                                  </div>
                                )}
                                
                                {entry.message && (
                                  <div className="text-sm text-gray-700 dark:text-gray-300 mb-2 whitespace-pre-wrap">
                                    {entry.message}
                                  </div>
                                )}
                                
                                {/* Show specific metadata details */}
                                {entry.entryTypeId === 5 && displayInfo.amount !== undefined && ( // Include on Invoice
                                  <div className="flex items-center space-x-4 text-sm">
                                    <span className="font-medium text-green-600 dark:text-green-400">
                                      Total: ${displayInfo.amount.toFixed(2)}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{displayInfo.isTaxable ? 'Taxable' : 'Non-taxable'}</span>
                                  </div>
                                )}
                                
                                {entry.entryTypeId === 2 && ( // Call
                                  <div className="flex items-center space-x-4 text-sm">
                                    {(() => {
                                      const metadata = JSON.parse(entry.metadata || '{}');
                                      const phoneNumber = metadata.phoneNumber || metadata.PhoneNumber;
                                      const contactPerson = metadata.contactPerson || metadata.ContactPerson;
                                      const duration = metadata.durationMinutes || metadata.DurationMinutes;
                                      const callType = metadata.callType || metadata.CallType;
                                      
                                      return (
                                        <>
                                          {phoneNumber && (
                                            <span className="text-gray-600 dark:text-gray-400">
                                              Phone: {phoneNumber}
                                            </span>
                                          )}
                                          {contactPerson && (
                                            <span className="text-gray-600 dark:text-gray-400">
                                              Contact: {contactPerson}
                                            </span>
                                          )}
                                          {duration && duration > 0 && (
                                            <span className="text-gray-600 dark:text-gray-400">
                                              Duration: {formatDuration(duration)}
                                            </span>
                                          )}
                                          {callType && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                              {callType}
                                            </span>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                )}
                                
                                {entry.entryTypeId === 6 && displayInfo.reason && ( // Status Update
                                  <div className="text-sm text-gray-600 dark:text-gray-400">
                                    Reason: {displayInfo.reason}
                                  </div>
                                )}
                                
                                {entry.entryTypeId === 4 && displayInfo.fileSize && ( // File Upload
                                  <div className="flex items-center space-x-4 text-sm">
                                    <span className="text-gray-600 dark:text-gray-400">
                                      Size: {formatFileSize(displayInfo.fileSize)}
                                    </span>
                                    {displayInfo.isConfidential && (
                                      <span className="text-xs text-red-500 dark:text-red-400">Confidential</span>
                                    )}
                                  </div>
                                )}
                                
                                {entry.entryTypeId === 7 && ( // Vendor Message
                                  <div className="space-y-1 text-sm">
                                    {displayInfo.priority && (
                                      <div className="flex items-center space-x-2">
                                        <span className="text-gray-600 dark:text-gray-400">Priority:</span>
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                          displayInfo.priority === 'Urgent' ? 'bg-red-100 text-red-800' :
                                          displayInfo.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                                          displayInfo.priority === 'Normal' ? 'bg-blue-100 text-blue-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {displayInfo.priority}
                                        </span>
                                      </div>
                                    )}
                                    {displayInfo.contactPerson && (
                                      <div className="text-gray-600 dark:text-gray-400">
                                        Contact: {displayInfo.contactPerson}
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {entry.entryTypeId === 8 && ( // Client Message
                                  <div className="space-y-1 text-sm">
                                    {displayInfo.priority && (
                                      <div className="flex items-center space-x-2">
                                        <span className="text-gray-600 dark:text-gray-400">Priority:</span>
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                          displayInfo.priority === 'Urgent' ? 'bg-red-100 text-red-800' :
                                          displayInfo.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                                          displayInfo.priority === 'Normal' ? 'bg-blue-100 text-blue-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {displayInfo.priority}
                                        </span>
                                      </div>
                                    )}
                                    {displayInfo.contactPerson && (
                                      <div className="text-gray-600 dark:text-gray-400">
                                        Contact: {displayInfo.contactPerson}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Attachments */}
                              {entry.attachments && entry.attachments.length > 0 && (
                                <div className="mt-3">
                                  {(() => {
                                    const imageAttachments = entry.attachments.filter(a => isImageFile(a.fileName));
                                    const docAttachments = entry.attachments.filter(a => !isImageFile(a.fileName));

                                    return (
                                      <>
                                        {/* Image Thumbnails Grid - Always 3 columns max for consistent sizing */}
                                        {imageAttachments.length > 0 && (
                                          <div className="grid grid-cols-3 gap-2 mb-2">
                                            {imageAttachments.map((attachment) => (
                                              <div
                                                key={attachment.id}
                                                className="relative group cursor-pointer rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:border-accent-color dark:hover:border-accent-color transition-colors"
                                                onClick={() => onViewImage && onViewImage(attachment.filePath, attachment.fileName)}
                                              >
                                                <div className="aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                                  <ImageThumbnail
                                                    filePath={attachment.filePath}
                                                    fileName={attachment.fileName}
                                                    attachmentId={attachment.id}
                                                  />
                                                </div>
                                                {/* File Type Badge */}
                                                <div className="absolute top-2 left-2">
                                                  {(() => {
                                                    const { badge, color } = getFileTypeBadge(attachment.fileName);
                                                    return (
                                                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${color}`}>
                                                        {badge}
                                                      </span>
                                                    );
                                                  })()}
                                                </div>
                                                {/* Action Buttons - Show on hover */}
                                                <div className="absolute top-2 right-2 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      onViewImage && onViewImage(attachment.filePath, attachment.fileName);
                                                    }}
                                                    className="p-1.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-md hover:bg-white dark:hover:bg-gray-800 transition-colors shadow-sm"
                                                    title="View"
                                                  >
                                                    <Eye className="h-3.5 w-3.5 text-gray-700 dark:text-gray-300" />
                                                  </button>
                                                  {onFileDownload && (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        onFileDownload(attachment.filePath, attachment.fileName);
                                                      }}
                                                      className="p-1.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-md hover:bg-white dark:hover:bg-gray-800 transition-colors shadow-sm"
                                                      title="Download"
                                                    >
                                                      <Download className="h-3.5 w-3.5 text-gray-700 dark:text-gray-300" />
                                                    </button>
                                                  )}
                                                  {onDeleteAttachment && (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDeleteAttachment(attachment);
                                                      }}
                                                      className="p-1.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors shadow-sm"
                                                      title="Delete"
                                                    >
                                                      <Trash2 className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                                                    </button>
                                                  )}
                                                </div>
                                                {/* Filename at bottom - Show on hover */}
                                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                  <div className="text-xs text-white font-medium truncate" title={attachment.fileName}>
                                                    {attachment.fileName}
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {/* Document Attachments */}
                                        {docAttachments.length > 0 && (
                                          <div className="space-y-2">
                                            {docAttachments.map((attachment) => (
                                              <div key={attachment.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group">
                                                <div className="flex items-center space-x-3 min-w-0 flex-1">
                                                  <div className="relative flex-shrink-0">
                                                    {(() => {
                                                      const { Icon, className } = getFileTypeIcon(attachment.fileName);
                                                      return <Icon className={`h-8 w-8 ${className}`} />;
                                                    })()}
                                                    {/* File Type Badge */}
                                                    <div className="absolute -top-1 -right-1">
                                                      {(() => {
                                                        const { badge, color } = getFileTypeBadge(attachment.fileName);
                                                        return (
                                                          <span className={`text-xs font-bold px-1 py-0.5 rounded ${color} shadow-sm`}>
                                                            {badge}
                                                          </span>
                                                        );
                                                      })()}
                                                    </div>
                                                  </div>
                                                  <div className="min-w-0 flex-1">
                                                    <div
                                                      className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate cursor-help"
                                                      title={attachment.fileName}
                                                    >
                                                      {attachment.fileName}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                      {formatFileSize(attachment.fileSize)}
                                                    </div>
                                                  </div>
                                                </div>
                                                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                  {isVideoFileName(attachment.fileName) && onViewImage && (
                                                    <button
                                                      type="button"
                                                      onClick={() => onViewImage(attachment.filePath, attachment.fileName)}
                                                      className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                                                      title="Preview"
                                                      aria-label="Preview video"
                                                    >
                                                      <Eye className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                                                    </button>
                                                  )}
                                                  {onFileDownload && (
                                                    <button
                                                      onClick={() => onFileDownload(attachment.filePath, attachment.fileName)}
                                                      className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                                                      title="Download"
                                                    >
                                                      <Download className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                                                    </button>
                                                  )}
                                                  {onDeleteAttachment && (
                                                    <button
                                                      onClick={() => onDeleteAttachment(attachment)}
                                                      className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors"
                                                      title="Delete"
                                                    >
                                                      <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                                                    </button>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TimelineTab;
